import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { useParadigm } from '../../contexts/ParadigmContext';

const TodoModal = ({ todo, users, onClose, onSuccess, defaultTripId, defaultExecutiveId }) => {
  const { labels } = useParadigm();
  const isEdit = !!todo;
  const [formData, setFormData] = useState({
    title: todo?.title || '',
    description: todo?.description || '',
    priority: todo?.priority || 'medium',
    assigned_to_id: todo?.assigned_to_id || '',
    trip_id: todo?.trip_id || defaultTripId || '',
    executive_id: todo?.executive_id || defaultExecutiveId || '',
    due_date: todo?.due_date ? todo.due_date.split('T')[0] : '',
    is_confidential: todo?.is_confidential || false
  });
  const [trips, setTrips] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        const res = await axios.get(`${apiBase}/trips?limit=200`, { headers });
        setTrips(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to load trips:', err);
      }
    };

    const fetchExecutives = async () => {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        const res = await axios.get(`${apiBase}/executives`, { headers });
        setExecutives(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to load executives:', err);
      }
    };

    fetchTrips();
    fetchExecutives();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const todoData = { ...formData };

      if (todoData.due_date) {
        todoData.due_date = new Date(todoData.due_date).toISOString();
      } else {
        delete todoData.due_date; // Remove the field instead of setting to null
      }

      // Remove empty assigned_to_id
      if (!todoData.assigned_to_id) {
        delete todoData.assigned_to_id; // Remove the field instead of setting to null
      }

      // Handle trip_id: when editing, keep empty string to signal removal; when creating, remove the field
      if (!todoData.trip_id) {
        if (!isEdit) {
          delete todoData.trip_id; // For create: remove the field
        }
        // For edit: keep empty string so backend removes the trip association
      }

      // Handle executive_id: when editing, keep empty string to signal removal; when creating, remove the field
      if (!todoData.executive_id) {
        if (!isEdit) {
          delete todoData.executive_id; // For create: remove the field
        }
        // For edit: keep empty string so backend removes the executive association
      }

      if (isEdit) {
        await axios.put(`${apiBase}/todos/${todo.id}`, todoData, { headers });
      } else {
        await axios.post(`${apiBase}/todos`, todoData, { headers });
      }
      onSuccess();
    } catch (err) {
      console.error('Todo operation error:', err);
      setError(`Failed to ${isEdit ? 'update' : 'create'} todo: ` + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ui-ink bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-ui-surface">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-ui-ink">
              {isEdit ? 'Edit Todo' : 'Create New Todo'}
            </h3>
            <button
              onClick={onClose}
              className="text-ui-muted hover:text-ui-ink-3 text-xl"
            >
              ×
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-ui-red-bg border border-ui-red-line rounded-md">
              <p className="text-sm text-ui-red-fg">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-1">
                Title <span className="text-ui-red-fg">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
                required
                placeholder="Enter task title..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
                rows="3"
                placeholder="Enter additional details..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-1">Assign To</label>
              <select
                value={formData.assigned_to_id}
                onChange={(e) => setFormData(prev => ({ ...prev, assigned_to_id: e.target.value }))}
                className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
              >
                <option value="">Assign to me</option>
                <option value="unassigned">Unassigned</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.email})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-1">Associate with Trip (Optional)</label>
              <select
                value={formData.trip_id}
                onChange={(e) => setFormData(prev => ({ ...prev, trip_id: e.target.value }))}
                className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
              >
                <option value="">No trip (Standalone)</option>
                {trips.map(trip => (
                  <option key={trip.id} value={trip.id}>
                    {trip.title}
                    {trip.start_date && ` (${new Date(trip.start_date).toLocaleDateString()})`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-1">{labels.associateWithExecutive}</label>
              <select
                value={formData.executive_id}
                onChange={(e) => setFormData(prev => ({ ...prev, executive_id: e.target.value }))}
                className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
              >
                <option value="">No executive</option>
                {executives.map(exec => (
                  <option key={exec.id} value={exec.id}>
                    {exec.first_name} {exec.last_name}
                    {exec.company_title && ` - ${exec.company_title}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-1">Due Date (Optional)</label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
              />
            </div>

            {/* Confidentiality Toggle */}
            <div className="border-t pt-4 mt-2">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-ui-ink-2">Confidential</label>
                  <p className="text-xs text-ui-muted mt-0.5">
                    Hide from team members (only visible to you, assignee, supervisors, and org admins)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, is_confidential: !prev.is_confidential }))}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-ui-amber-solid focus:ring-offset-2 ${
                    formData.is_confidential ? 'bg-ui-amber-strong' : 'bg-ui-line'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-ui-surface shadow ring-0 transition duration-200 ease-in-out ${
                      formData.is_confidential ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              {formData.is_confidential && (
                <div className="mt-2 text-xs text-ui-amber-fg bg-ui-amber-bg rounded px-2 py-1">
                  {formData.trip_id
                    ? 'This todo will only be visible to you, the assignee, trip collaborators, supervisors, and org admins.'
                    : 'This todo will only be visible to you, the assignee, supervisors, and org admins.'}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-ui-ink-3 bg-ui-hover rounded-md hover:bg-ui-line"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.title.trim()}
                className="px-4 py-2 bg-ui-primary hover:bg-ui-primary-hover text-ui-on-primary rounded-md disabled:opacity-50"
              >
                {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Todo' : 'Create Todo')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TodoModal;
